/**
 * @module use-cases/checkout/slice
 * @desc Checkout Slice (Reducers and ExtraReducers)
 *
 */

import { type Checkout, type Payments, Utilities, CHECKOUT_VIEW } from 'core/domains';
import { actions as actionsOrder } from 'core/use-cases/order';
import { createSlice } from 'core/use-cases/utilities';
import { getLocationPathname } from 'core/utilities';

import { actions } from '../actions';
import { canSubmit, isCheckoutRunning, updateStepCompleted } from './helpers';
import { initialState } from './initial-state';

const activeReview = (state: Checkout) => {
  state.views.payment.isCreditCardSelected = false;
  state.views.payment.canSubmit = true;
  state.views.payment.completed = true;
  state.views.summary.canSubmit = true;
  state.views.summary.completed = true;
};

const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder.addCase(actions.checkoutCleanUp, () => initialState);

    builder.addCase(actions.selectedGiftsCreate, (state) => {
      state.selectedPromotions =
        Array.from({ length: state?.rewards?.data?.choose?.length || 0 }) || [];
    });

    builder.addCase(actions.promotionFetch.failure, (state) => {
      state.isGiftSelected = false;
      state.isRunning = false;
    });

    builder.addCase(actions.promotionFetch.request, (state) => {
      state.isGiftSelected = false;
    });

    builder.addCase(actions.promotionFetch.success, (state) => {
      state.isGiftSelected = true;
    });

    builder.addCase(actions.giftItemUpdate, (state, { payload }) => {
      if (!payload) {
        return;
      }

      const alreadySelectedPromotion = state.selectedPromotions[payload?.index];

      if (!alreadySelectedPromotion) {
        state.selectedPromotions[payload?.index] = {
          giftNumber: payload?.giftNumber || null,
          giftQuantity: payload?.giftQuantity || null,
          giftItemNumber: payload?.giftItemNumber || null,
          promotionCode: payload?.promotionCode || null,
          nextOrderAllowed: payload?.nextOrderAllowed || false,
          rewards: payload?.rewards || [],
        };
        return;
      }

      const alreadySelectedItem = alreadySelectedPromotion?.rewards.find(
        ({ sku }) => sku === payload?.rewards[0].sku,
      );

      if (!alreadySelectedItem) {
        alreadySelectedPromotion.rewards.push(payload.rewards[0]);
        return;
      }

      if (payload?.method === 'add') {
        alreadySelectedItem.quantity++;
        return;
      }

      if (payload?.method === 'remove') {
        alreadySelectedItem.quantity--;

        alreadySelectedItem.quantity = Math.max(0, alreadySelectedItem.quantity);
        return;
      }

      const selectedAmount = alreadySelectedPromotion?.rewards?.reduce(
        (sum, { quantity }) => sum + quantity,
        0,
      );

      const remainingValue = alreadySelectedPromotion.giftQuantity - selectedAmount;
      const sentQuantity = payload.rewards[0].quantity;

      alreadySelectedItem.quantity = Math.min(
        sentQuantity,
        selectedAmount + remainingValue,
        alreadySelectedPromotion.giftQuantity,
        alreadySelectedItem.quantity + remainingValue,
      );
    });

    builder.addCase(actions.processFetch.request, (state) => {
      state.rewards.data = {
        discounts: [],
        gifts: [],
        choose: [],
      };
      state.rewards.message = '';
      state.rewards.status = Utilities.STATUS.RUNNING;
      state.isNotificationVisible = false;
      state.isRetry = false;
      state.selectedPromotions = [];

      state.paymentMethods.data = {} as Checkout['paymentMethods']['data'];
      state.paymentMethods.status = Utilities.STATUS.IDLE;
    });

    builder.addCase(actions.processFetch.failure, (state, { error }) => {
      const { message } = error;

      state.rewards.message = message;
      state.rewards.status = Utilities.STATUS.ERROR;
    });

    builder.addCase(actions.processFetch.success, (state, { meta, payload }) => {
      state.rewards.data = payload as unknown as Checkout['rewards']['data'];
      state.rewards.message = '';
      state.rewards.status = Utilities.STATUS.SUCCESS;
      state.rewards.updatedAt = meta.updatedAt;
      state.isRunning = false;
    });

    builder.addCase(actions.setReady, (state, { payload: isCheckoutReady }) => {
      state.isReady = isCheckoutReady!;
      state.views.cart.canSubmit = isCheckoutReady!;
      state.views.cart.completed = isCheckoutReady!;
      state.isNotificationVisible = false;
    });

    builder.addCase(actions.setView, (state, { payload: view }) => {
      state.views.payment.view = view;
    });

    builder.addCase(actions.toggleNotification, (state) => {
      state.isNotificationVisible = !state.isNotificationVisible;
    });

    builder.addCase(actions.setStep, (state, { payload: step }) => {
      state.isNotificationVisible = false;
      state.isRetry = false;
      const pathname = getLocationPathname();

      if (!Object.keys(state.paymentOption).length && pathname !== '/carrinho') {
        state.step = 'payment';
      }

      const isPaymentStepCompleted =
        step && state.views.payment.canSubmit && !state.views.payment.isCreditCardSelected;

      if (isPaymentStepCompleted) {
        if (pathname !== '/carrinho') {
          updateStepCompleted(state, step);

          state.step = step;
        }
      }

      if (step === 'cart') {
        state.step = 'cart';
      }
    });

    builder.addCase(actions.setCentralPaymentOption, (state, { payload }) => {
      if (payload) {
        const {
          paymentMethods: {
            data: { centralPayment },
          },
        } = state;

        const payment =
          centralPayment?.paymentInfo.find(({ paymentCode }) => paymentCode === payload) ||
          ({} as Checkout['paymentMethods']['data']['bankSlip']['paymentInfo'][number]);

        state.paymentOption = {
          ...payment,
          paymentMethod: centralPayment?.name || 'centralPayment',
          paymentPlanType: centralPayment?.paymentPlanType || 'centralPayment',
        };

        activeReview(state);
      }
    });

    builder.addCase(actions.setPayWithdrawalsOption, (state, { payload }) => {
      if (payload) {
        const {
          paymentMethods: {
            data: { linkPayment },
          },
        } = state;

        const payment =
          linkPayment?.paymentInfo.find(({ paymentCode }) => paymentCode === payload) ||
          ({} as Checkout['paymentMethods']['data']['bankSlip']['paymentInfo'][number]);

        state.paymentOption = {
          ...payment,
          paymentMethod: linkPayment?.name || 'linkPayment',
          paymentPlanType: linkPayment?.paymentPlanType || 'linkPayment',
        };

        activeReview(state);
      }
    });

    builder.addCase(actions.setBankSlipOption, (state, { payload }) => {
      if (payload) {
        const {
          paymentMethods: {
            data: { bankSlip },
          },
        } = state;

        const payment =
          bankSlip.paymentInfo.find(({ paymentCode }) => paymentCode === payload) ||
          ({} as Checkout['paymentMethods']['data']['bankSlip']['paymentInfo'][number]);

        state.paymentOption = {
          ...payment,
          paymentMethod: bankSlip.name || 'Boleto',
          paymentPlanType: bankSlip?.paymentPlanType || 'bankBilling',
        };

        activeReview(state);
      }
    });

    builder.addCase(actions.setPaymentOption, (state, { payload }) => {
      if (!payload) {
        state.paymentOption = {};
        return;
      }
      const {
        paymentMethods: { data },
      } = state;

      const paymentOptions = Object.keys(data).flatMap(
        (key) => (data as Record<string, Payments>)[key].paymentInfo,
      );

      const payment = paymentOptions?.find((item) => item?.paymentCode === payload.code) || {};

      state.paymentOption = {
        ...payment,
        paymentMethod: payment.name,
        paymentPlanType: payload?.planType,
        paymentMode: payload?.mode,
      };

      activeReview(state);
    });

    builder.addCase(actions.setCreditCardCompleted, (state, { payload }) => {
      if (payload) {
        const {
          paymentMethods: {
            data: { creditCard },
          },
        } = state;

        const paymentInfo = creditCard.paymentInfo.flatMap(({ info }) => info);

        const payment = paymentInfo?.find(
          ({ flagId, installments }) =>
            installments === Number(payload.installments) && flagId === payload.creditCardSchema,
        );

        state.paymentOption = {
          ...state.paymentOption,
          ...payment,
          ...payload,
          name: `${creditCard.name} em ${(payment?.name || '').toLowerCase()}`,
        };

        state.views.payment.canSubmit = true;
        state.views.payment.completed = true;
        state.views.payment.isCreditCardSelected = false;
        state.views.payment.view = CHECKOUT_VIEW.PAYMENT_SELECTOR;
      }
    });

    builder.addCase(actions.setCreditCardOption, (state, { payload }) => {
      if (payload) {
        const {
          paymentMethods: {
            data: { creditCard },
          },
        } = state;

        state.paymentOption = {
          ...payload,
          name: creditCard?.name,
          paymentCode: creditCard?.paymentPlanType,
          paymentMethod: creditCard?.paymentPlanType,
          paymentPlanType: creditCard?.paymentPlanType,
        };

        state.views.payment.canSubmit = true;
        state.views.payment.completed = false;
        state.views.payment.isCreditCardSelected = true;
      }
    });

    builder.addCase(actions.setPaymentMethodStatus, (state) => {
      state.paymentMethods.status = Utilities.STATUS.IDLE;
      state.isGiftSelected = false;
      state.rewards.status = Utilities.STATUS.IDLE;
    });

    builder.addCase(actions.createOrderFetch.request, (state) => {
      state.order.errno = null;
      state.order.message = '';
      state.order.status = Utilities.STATUS.RUNNING;

      state.isRunning = isCheckoutRunning(state);
      state.isRetry = false;
    });

    builder.addCase(actions.createOrderFetchNewFlow.request, (state) => {
      state.order.errno = null;
      state.order.message = '';
      state.order.status = Utilities.STATUS.RUNNING;

      state.isRunning = isCheckoutRunning(state);
      state.isRetry = false;
    });

    builder.addCase(actions.createOrderFetch.success, (state, { meta, payload: code }) => {
      state.order.code = code;
      state.order.message = '';
      state.order.updatedAt = meta.updatedAt;
      state.order.status = Utilities.STATUS.SUCCESS;

      state.isRunning = isCheckoutRunning(state);
      state.views.payment.canSubmit = canSubmit(state);
      state.views.summary.canSubmit = canSubmit(state);
      state.isRetry = false;
    });

    builder.addCase(actions.createOrderFetchNewFlow.success, (state, { meta, payload: code }) => {
      state.order.code = code;
      state.order.message = '';
      state.order.updatedAt = meta.updatedAt;
      state.order.status = Utilities.STATUS.SUCCESS;

      state.isRunning = isCheckoutRunning(state);
      state.views.payment.canSubmit = canSubmit(state);
      state.views.summary.canSubmit = canSubmit(state);
      state.isRetry = false;
    });

    builder.addCase(actions.createOrderFetch.failure, (state, { error }) => {
      const { message } = error;

      state.order.message = message;
      state.order.status = Utilities.STATUS.ERROR;

      state.isRunning = isCheckoutRunning(state);
      state.views.payment.canSubmit = canSubmit(state);
      state.views.summary.canSubmit = canSubmit(state);
      state.isRetry = false;
    });

    builder.addCase(actions.createOrderFetchNewFlow.failure, (state, { error }) => {
      const { message } = error;

      state.order.message = message;
      state.order.status = Utilities.STATUS.ERROR;

      state.isRunning = isCheckoutRunning(state);
      state.views.payment.canSubmit = canSubmit(state);
      state.views.summary.canSubmit = canSubmit(state);
      state.isRetry = false;
    });

    builder.addCase(actions.paymentDetailFetch.request, (state) => {
      state.paymentDetail.message = '';
      state.paymentDetail.status = Utilities.STATUS.RUNNING;

      state.isRunning = isCheckoutRunning(state);
      state.views.payment.canSubmit = canSubmit(state);
      state.views.summary.canSubmit = canSubmit(state);
      state.isRetry = false;
    });

    builder.addCase(actions.paymentDetailNewFlowFetch.request, (state) => {
      state.paymentDetail.message = '';
      state.isLoadingPaymentPlan = true;
      state.paymentPlanError = false;
    });

    builder.addCase(actions.paymentDetailFetch.failure, (state, { error }) => {
      const { message } = error;

      state.paymentDetail.message = message;
      state.paymentDetail.status = Utilities.STATUS.ERROR;

      state.isRunning = isCheckoutRunning(state);
      state.views.payment.canSubmit = true;
      state.views.summary.canSubmit = canSubmit(state);
      state.isRetry = true;
    });

    builder.addCase(actions.paymentDetailNewFlowFetch.failure, (state, { error }) => {
      state.isLoadingPaymentPlan = false;
      state.paymentPlanError = true;
    });

    builder.addCase(actions.paymentDetailNewFlowFetch.success, (state) => {
      state.isLoadingPaymentPlan = false;
      state.paymentPlanError = false;
    });

    builder.addCase(actions.paymentMethodsFetch.request, (state) => {
      state.paymentMethods.message = '';
      state.paymentMethods.status = Utilities.STATUS.RUNNING;

      state.paymentDetail.data = {} as Checkout['paymentDetail']['data'];
      state.paymentDetail.message = '';
      state.paymentDetail.status = Utilities.STATUS.IDLE;

      state.isRunning = isCheckoutRunning(state);
      state.views.payment.canSubmit = canSubmit(state);
      state.views.summary.canSubmit = canSubmit(state);
      state.isRetry = false;
    });

    builder.addCase(actions.paymentMethodsFetch.success, (state, { meta, payload }) => {
      state.paymentMethods.data = payload;
      state.paymentMethods.updatedAt = meta.updatedAt;
      state.paymentMethods.status = Utilities.STATUS.SUCCESS;

      state.isRunning = isCheckoutRunning(state);
      state.views.payment.canSubmit = canSubmit(state);
      state.views.summary.canSubmit = canSubmit(state);
    });

    builder.addCase(actions.paymentMethodsFetch.failure, (state, { error }) => {
      const { message } = error;

      state.paymentMethods.data = {} as Checkout['paymentMethods']['data'];
      state.paymentMethods.message = message;
      state.paymentMethods.status = Utilities.STATUS.ERROR;

      state.isRunning = isCheckoutRunning(state);
      state.views.payment.canSubmit = canSubmit(state);
      state.views.summary.canSubmit = canSubmit(state);
      state.step = 'payment';
    });

    builder.addCase(actionsOrder.orderFetch.success, (state, { payload }) => {
      const { status } = payload;

      state.isRetry = status === 'capture' ? true : false;
    });

    builder.addCase(actionsOrder.orderFetch.failure, (state) => {
      state.isRetry = false;
    });
  },
});

export const { reducer } = checkoutSlice;
